/**
 * WORLD-CUP-BRAZIL-GROWTH-P1 — Influencer White Label registry.
 * Scalable per-country roster. Each entry powers /brasil/influencer/:slug
 * (and future MX/AR/IN equivalents) without code changes.
 * NO fake metrics: counters resolve from existing referral engine at runtime.
 */
export interface InfluencerProfile {
  slug: string;
  country: "BR" | "MX" | "AR" | "IN";
  displayName: string;
  handle: string;
  emoji: string;
  /** Public referral-code suffix used to seed the link. Final code resolves through referrals.ts. */
  referralSeed: string;
  /** Accent tint applied as overlay only — never replaces global brand. */
  accent: string;
  heroTagline: string;
  ctaJoin: string;
  communityLabel: string;
  /** Optional language hint (BCP-47). Defaults to country language. */
  language?: string;
  /** Optional theme key for future visual variants. */
  theme?: "default" | "minimal" | "bold";
  /** Optional primary brand color override. */
  primaryColor?: string;
  /** Optional explicit referral code (overrides seed). */
  referralCode?: string;
  /** Marks placeholder profiles awaiting onboarding. */
  placeholder?: boolean;
}

export const INFLUENCERS: Record<string, InfluencerProfile> = {
  casimiro: {
    slug: "casimiro",
    country: "BR",
    displayName: "Casimiro",
    handle: "@casimiro",
    emoji: "🎙️",
    referralSeed: "CASIMIRO",
    accent: "oklch(0.7 0.18 25)",
    heroTagline: "Entre para a torcida do Casimiro",
    ctaJoin: "🔥 Entrar na torcida",
    communityLabel: "Torcida Casimiro",
  },
  futebol: {
    slug: "futebol",
    country: "BR",
    displayName: "Futebol Brasil",
    handle: "@futebolbr",
    emoji: "⚽",
    referralSeed: "FUTEBOL",
    accent: "oklch(0.6 0.18 145)",
    heroTagline: "Junte-se à comunidade Futebol Brasil",
    ctaJoin: "⚽ Entrar na comunidade",
    communityLabel: "Comunidade Futebol Brasil",
  },
  torcidasp: {
    slug: "torcidasp",
    country: "BR",
    displayName: "Torcida SP",
    handle: "@torcidasp",
    emoji: "🟡",
    referralSeed: "TORCIDASP",
    accent: "oklch(0.8 0.18 90)",
    heroTagline: "A torcida paulista no VerseSport",
    ctaJoin: "🟡 Entrar na torcida SP",
    communityLabel: "Torcida SP",
  },
};

/* ---- MX — named roster (onboarding-ready, real-style slugs) ---- */
const MX_ACCENT_RED = "oklch(0.65 0.22 25)";
const MX_ACCENT_GREEN = "oklch(0.55 0.18 145)";
const MX_ROSTER: Array<{ slug: string; displayName: string; handle: string; emoji: string; accent: string; tagline: string; cta: string; community: string }> = [
  { slug: "mx-tri",     displayName: "Porra del Tri",    handle: "@porradeltri",  emoji: "🇲🇽", accent: MX_ACCENT_GREEN, tagline: "Súmate a la Porra del Tri en VerseSport México", cta: "🇲🇽 Únete a la porra", community: "Porra del Tri" },
  { slug: "mx-clasico", displayName: "Clásico Nacional", handle: "@clasicomx",    emoji: "⚔️", accent: MX_ACCENT_RED,   tagline: "América vs Chivas: ¿de qué lado estás?",          cta: "⚔️ Elige tu lado",     community: "Comunidad Clásico" },
  { slug: "mx-regio",   displayName: "Clásico Regio",    handle: "@clasicoregio", emoji: "🏔️", accent: MX_ACCENT_RED,   tagline: "Monterrey vs Tigres en VerseSport México",        cta: "🔥 Entrar al Regio",   community: "Comunidad Regia" },
];
for (const r of MX_ROSTER) {
  INFLUENCERS[r.slug] = {
    slug: r.slug,
    country: "MX",
    displayName: r.displayName,
    handle: r.handle,
    emoji: r.emoji,
    referralSeed: r.slug.toUpperCase().replace(/-/g, ""),
    accent: r.accent,
    heroTagline: r.tagline,
    ctaJoin: r.cta,
    communityLabel: r.community,
    language: "es-MX",
    theme: "default",
    placeholder: true,
  };
}

/* ---- AR placeholders — ready for onboarding without code changes ---- */
const PLACEHOLDER_COUNTRIES: Array<{ country: InfluencerProfile["country"]; accent: string; greeting: string }> = [
  { country: "AR", accent: "oklch(0.78 0.10 220)", greeting: "Sumate a la comunidad" },
];
for (const { country, accent, greeting } of PLACEHOLDER_COUNTRIES) {
  for (let i = 1; i <= 3; i++) {
    const slug = `${country.toLowerCase()}-creador-${i}`;
    INFLUENCERS[slug] = {
      slug,
      country,
      displayName: `Creator ${country} ${i}`,
      handle: `@creator${country.toLowerCase()}${i}`,
      emoji: "🎙️",
      referralSeed: `${country}CREATOR${i}`,
      accent,
      heroTagline: `${greeting} ${country} ${i}`,
      ctaJoin: greeting,
      communityLabel: `Community ${country} ${i}`,
      theme: "default",
      placeholder: true,
    };
  }
}

/* ---- IN — named slots ready for onboarding without code changes ---- */
const IN_ACCENT_SAFFRON = "oklch(0.72 0.16 60)";
const IN_ACCENT_GREEN = "oklch(0.55 0.18 145)";
const IN_ROSTER: Array<{ slug: string; displayName: string; handle: string; emoji: string; accent: string; tagline: string; cta: string; community: string }> = [
  { slug: "in-creator-1", displayName: "Creator IN 1", handle: "@creatorin1", emoji: "🟠", accent: IN_ACCENT_SAFFRON, tagline: "Join the IN 1 community on VerseSport India", cta: "🔥 Join the community", community: "IN Creator 1 Community" },
  { slug: "in-creator-2", displayName: "Creator IN 2", handle: "@creatorin2", emoji: "⚪", accent: IN_ACCENT_SAFFRON, tagline: "Join the IN 2 community on VerseSport India", cta: "🔥 Join the community", community: "IN Creator 2 Community" },
  { slug: "in-creator-3", displayName: "Creator IN 3", handle: "@creatorin3", emoji: "🟢", accent: IN_ACCENT_GREEN, tagline: "Join the IN 3 community on VerseSport India", cta: "🔥 Join the community", community: "IN Creator 3 Community" },
  { slug: "in-sports-1", displayName: "Sports IN 1", handle: "@sportsin1", emoji: "⚽", accent: IN_ACCENT_GREEN, tagline: "India Sports 1 — predictions & community", cta: "⚽ Join the squad", community: "IN Sports 1 Squad" },
  { slug: "in-sports-2", displayName: "Sports IN 2", handle: "@sportsin2", emoji: "🏏", accent: IN_ACCENT_SAFFRON, tagline: "India Sports 2 — predictions & community", cta: "🏏 Join the squad", community: "IN Sports 2 Squad" },
];
for (const r of IN_ROSTER) {
  INFLUENCERS[r.slug] = {
    slug: r.slug,
    country: "IN",
    displayName: r.displayName,
    handle: r.handle,
    emoji: r.emoji,
    referralSeed: r.slug.toUpperCase().replace(/-/g, ""),
    accent: r.accent,
    heroTagline: r.tagline,
    ctaJoin: r.cta,
    communityLabel: r.community,
    language: "en-IN",
    theme: "default",
    placeholder: true,
  };
}

export function getInfluencer(slug: string | undefined): InfluencerProfile | null {
  if (!slug) return null;
  return INFLUENCERS[slug.toLowerCase()] ?? null;
}

export function listInfluencersByCountry(country: string): InfluencerProfile[] {
  return Object.values(INFLUENCERS).filter((i) => i.country === country.toUpperCase());
}